import React, { useEffect, useState } from "react";
import axios from "axios";
import "./Cards.css";
import CardItem from "./CardItem";
import Pagination from "@mui/material/Pagination";
import { Select, MenuItem, FormControl, InputLabel, Box, Typography } from "@mui/material";
import { fetchImage } from "../../services/getProducts";

const ITEMS_PER_PAGE_OPTIONS = [24, 36, 48, 60];

function getNumberOfPages(products, itemsPerPage) {
  return Math.ceil(products.length / itemsPerPage);
}

function getProductsToDisplay(products, page, itemsPerPage) {
  const startIndex = (page - 1) * itemsPerPage;
  return products.slice(startIndex, startIndex + itemsPerPage);
}

function Cards({ products }) {
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);
  const [displayedProducts, setDisplayedProducts] = useState([]);

  // Recuperar configuración guardada en localStorage al iniciar
  useEffect(() => {
    const savedItemsPerPage = localStorage.getItem("itemsPerPage");
    if (savedItemsPerPage) {
      setItemsPerPage(parseInt(savedItemsPerPage));
    }
  }, []);

  // Resetear página cuando cambien los productos (nueva búsqueda/filtro)
  useEffect(() => {
    setPageNumber(1);
  }, [products]);

  // Resetear página cuando cambie itemsPerPage
  useEffect(() => {
    setPageNumber(1);
    localStorage.setItem("itemsPerPage", itemsPerPage.toString());
  }, [itemsPerPage]);

  // Actualizar productos mostrados cuando cambie la página, productos o items por página
  useEffect(() => {
    const productsToShow = getProductsToDisplay(products, pageNumber, itemsPerPage);
    setDisplayedProducts(productsToShow);
    localStorage.setItem("savedProducts", JSON.stringify(productsToShow));
  }, [pageNumber, products, itemsPerPage]);

  const handleChangePageNumber = (event, value) => {
    setPageNumber(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    // No necesitamos setPageNumber(1) aquí porque ya se maneja en el useEffect
  };

  const totalPages = getNumberOfPages(products, itemsPerPage);

  return (
    <div>
      {/* Selector de productos por página */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginBottom: '2.5rem',
          gap: 1.5,
        }}
      >
        <Typography variant="body2" sx={{ color: '#9a9a9e' }}>
          Mostrar
        </Typography>
        <FormControl size="small" sx={{ minWidth: 72 }}>
          <Select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            sx={{
              color: '#f2f2f2',
              fontSize: '0.85rem',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.18)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.4)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#faad2b',
              },
              '& .MuiSvgIcon-root': { color: '#9a9a9e' },
            }}
            MenuProps={{
              PaperProps: {
                sx: { backgroundColor: '#161618', color: '#f2f2f2' },
              },
            }}
          >
            {ITEMS_PER_PAGE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" sx={{ color: '#9a9a9e' }}>
          productos por página
        </Typography>
      </Box>

      <div className="cards">
        <div className="cards__container">
          <CardGrid cardsToDisplay={displayedProducts} />
        </div>
      </div>

      {/* Mostrar paginación solo si hay más de una página */}
      {totalPages > 1 && (
        <Pagination
          count={totalPages}
          shape="rounded"
          showFirstButton
          showLastButton
          page={pageNumber}
          onChange={handleChangePageNumber}
          sx={{
            marginTop: "3rem",
            marginBottom: "3rem",
            display: "flex",
            justifyContent: "center",
            "& .MuiPaginationItem-root": {
              color: "#d0d0d3",
              margin: "0 3px",
              fontSize: "0.95rem",
              borderRadius: "6px",
            },
            "& .Mui-selected": {
              backgroundColor: "#f2f2f2",
              color: "#0b0b0d",
              fontWeight: "600",
            },
            "& .MuiPaginationItem-ellipsis": {
              color: "#6f6f74",
            },
            "& .MuiPaginationItem-root:hover": {
              backgroundColor: "rgba(255,255,255,0.08)",
            },
          }}
        />
      )}

      {/* Información adicional */}
      <Box sx={{ textAlign: 'center', marginBottom: '20px' }}>
        <Typography variant="body2" sx={{ color: '#6f6f74' }}>
          Mostrando {displayedProducts.length} de {products.length} productos
        </Typography>
      </Box>
    </div>
  );
}

function getAvailableQuantity(stockReel) {
  if (!Array.isArray(stockReel) || stockReel.length === 0) return 0;
  return Math.max(0, ...stockReel.map((item) => parseInt(item.real, 10) || 0));
}

const CardGrid = ({ cardsToDisplay }) => {
  const [updatedCards, setUpdatedCards] = useState([]);

  useEffect(() => {
    const updateCards = async () => {
      const savedProducts =
        JSON.parse(localStorage.getItem("savedProducts")) || [];
      const updatedCards = await Promise.all(
        cardsToDisplay.map(async (product) => {
          if (
            savedProducts.some(
              (savedProduct) => savedProduct.id === product.id
            )
          ) {
            const newImg = await fetchImage(product.id);
            return { ...product, img: newImg || product.img };
          }
          return product;
        })
      );
      setUpdatedCards(updatedCards);
    };

    updateCards();
  }, [cardsToDisplay]);

  return (
    <div className="cards__wrapper">
      <ul className="cards__items">
        {updatedCards.map((product) => (
          <CardItem
            key={product.id}
            id={product.id}
            src={product.img}
            product={product.label}
            price={`₡${Number(product.price).toFixed(0)}`}
            tag={product.tag}
            label={product.label}
            available={getAvailableQuantity(product.stock_reel)}
          />
        ))}
      </ul>
    </div>
  );
};

export default Cards;